import React from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

import { GET_KEY, GET_PROJECT, UPDATE_PROJECT } from '../../apis/project';
import Button from '../atoms/button';

const propTypes = {
  openToggle: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
};

const verifyKey = async (getKey, key, id) => {
  const { data, loading, error } = await getKey({
    variables: {
      input: {
        key,
        id,
      },
    },
  });
  if (data && !loading && !error) {
    const { getProjectKey } = data;
    return getProjectKey;
  }
  return false;
};
const ModalEditProject = ({ openToggle, handleClose, projectId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [updateProject] = useMutation(UPDATE_PROJECT);
  const [getKey] = useLazyQuery(GET_KEY);
  const { data } = useQuery(GET_PROJECT, {
    variables: {
      input: {
        id: projectId,
      },
    },
  });
  const modalTitle = 'Edit Project';
  const closeButton = 'Cancel';
  const createProject = 'Update Project';
  const validationSchema = Yup.object().shape({
    projectName: Yup.string().required('Project name is required.'),
    projectKey: Yup.string()
      .required('Project key is required.')
      .matches(/^[A-Z]{2,4}$/, 'Project key must be uppercase letters and 2 to 4 characters long')
      .min(2, 'Project key must be 2 to 4 characters long')
      .max(4, 'Project key must be 2 to 4 characters long')
      .test('test-key', 'Key Already Used', async (value) => {
        if (value) {
          const verification = await verifyKey(getKey, value, projectId);
          if (verification) {
            return false;
          }
          return true;
        }
        return true;
      }),
    description: Yup.string().max(255, 'Maximum 255 characters allowed'),
  });
  return (
    <Formik
      initialValues={{
        projectName: data.project.name || '',
        projectKey: data.project.key || '',
        description: data.project.description || '',
      }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        const variables = {
          input: {
            id: parseInt(projectId, 10),
            name: values.projectName,
            key: values.projectKey,
            description: values.description,
            isActive: true,
          },
        };
        const {
          data: { updateProject: updated },
        } = await updateProject({
          variables,
        });
        if (updated) navigate('/');
      }}
    >
      {({ handleSubmit, handleChange, handleBlur, touched, errors, values }) => (
        <form action="/project">
          <Dialog
            className="custom-modal-dialog"
            fullScreen={fullScreen}
            open={openToggle}
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogTitle id="responsive-dialog-title">{modalTitle}</DialogTitle>
            <DialogContent className="dialog-content">
              <div className="custom-form-group">
                <Typography className="custom-label" variant="caption" display="block" gutterBottom>
                  Project Name
                </Typography>
                <TextField
                  className="custom-form-input"
                  margin="dense"
                  id="projectName"
                  type="text"
                  placeholder="Enter here"
                  fullWidth
                  required
                  name="projectName"
                  value={values.projectName || ''}
                  error={errors.projectName && touched.projectName}
                  helperText={touched.projectName && errors.projectName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              <div className="custom-form-group">
                <Typography className="custom-label" variant="caption" display="block" gutterBottom>
                  Project Key
                </Typography>

                <TextField
                  className="custom-form-input"
                  margin="dense"
                  id="projectKey"
                  type="text"
                  fullWidth
                  placeholder="Enter here"
                  required
                  name="projectKey"
                  value={values.projectKey || ''}
                  error={errors.projectKey && touched.projectKey}
                  helperText={touched.projectKey && errors.projectKey}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              <div className="custom-form-group">
                <Typography
                  className="custom-label"
                  variant="caption"
                  display="inline-block"
                  gutterBottom
                >
                  Description
                </Typography>

                <TextField
                  className="custom-form-input"
                  margin="dense"
                  id="projectDescription"
                  type="text"
                  multiline
                  rows={4}
                  fullWidth
                  placeholder="Enter here"
                  name="description"
                  value={values.description || ''}
                  error={errors.description && touched.description}
                  helperText={touched.description && errors.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
            </DialogContent>
            <DialogActions className="dialog-actions" style={{ justifyContent: 'flex-start' }}>
              <div className="btn-group">
                <Button primary onClick={() => handleSubmit()} autoFocus>{createProject}</Button>
                <Button primary outlined onClick={handleClose}>{closeButton}</Button>
              </div>
            </DialogActions>
          </Dialog>
        </form>
      )}
    </Formik>
  );
};

ModalEditProject.propTypes = propTypes;
export default ModalEditProject;
